import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { accountsPayableService, APSupplierBalanceDTO } from '../../../../services/accountingService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { Wallet, Eye } from 'lucide-react';

const AccountsPayablePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();

  const [balances, setBalances] = useState<APSupplierBalanceDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    accountsPayableService.getSupplierBalances()
      .then((res) => {
        const data = (res?.data as any)?.data ?? res?.data ?? [];
        setBalances(Array.isArray(data) ? data : []);
      })
      .finally(() => setLoading(false));
  }, []);

  const totals = balances.reduce((acc, b) => ({
    outstanding: acc.outstanding + b.outstandingBalance,
    current: acc.current + b.current,
    d30: acc.d30 + b.days1To30,
    d60: acc.d60 + b.days31To60,
    d90: acc.d90 + b.days61To90,
    over90: acc.over90 + b.daysOver90,
  }), { outstanding: 0, current: 0, d30: 0, d60: 0, d90: 0, over90: 0 });

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 1100, margin: '0 auto' }}>
        <div className="ims-list-header">
          <h1 className="ims-list-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Wallet size={20} style={{ color: 'var(--ims-accent)' }} />
            {t('accounting.ap.title')}
          </h1>
        </div>

        <div className="ims-stats-row" style={{ marginBottom: 20 }}>
          <div className="ims-stat-card">
            <div className="ims-stat-info">
              <span className="ims-stat-value">{fmt(totals.outstanding)}</span>
              <span className="ims-stat-label">{t('accounting.ap.totalOutstanding')}</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
          </div>
        ) : balances.length === 0 ? (
          <div className="ims-card" style={{ textAlign: 'center', padding: 40, color: 'var(--ims-text-muted)' }}>
            {t('accounting.ap.noBalances')}
          </div>
        ) : (
          <div className="ims-card">
            <div className="ims-table-scroll">
              <table className="ims-table">
                <thead>
                  <tr>
                    <th className="ims-th">{t('accounting.ap.supplier')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 100 }}>{t('accounting.aging.current')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 100 }}>{t('accounting.aging.d1to30')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 100 }}>{t('accounting.aging.d31to60')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 100 }}>{t('accounting.aging.d61to90')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 100 }}>{t('accounting.aging.over90')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 130 }}>{t('accounting.ap.balance')}</th>
                    <th className="ims-th" style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {balances.map((b) => (
                    <tr key={b.supplierId} className="ims-tr">
                      <td className="ims-td"><strong>{b.supplierName}</strong></td>
                      <td className="ims-td ims-td--line-total">{fmt(b.current)}</td>
                      <td className="ims-td ims-td--line-total">{fmt(b.days1To30)}</td>
                      <td className="ims-td ims-td--line-total">{fmt(b.days31To60)}</td>
                      <td className="ims-td ims-td--line-total">{fmt(b.days61To90)}</td>
                      <td className="ims-td ims-td--line-total" style={{ color: b.daysOver90 > 0 ? 'var(--ims-red-text)' : undefined }}>{fmt(b.daysOver90)}</td>
                      <td className="ims-td ims-td--line-total"><strong>{fmt(b.outstandingBalance)}</strong></td>
                      <td className="ims-td">
                        <button className="grid-action-btn-view" title={t('accounting.ap.viewLedger')}
                          onClick={() => navigate(`/accounting/accounts-payable/suppliers/${b.supplierId}`)}>
                          <Eye size={16} strokeWidth={1.75} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="ims-td"><strong>{t('accounting.gl.total')}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(totals.current)}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(totals.d30)}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(totals.d60)}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(totals.d90)}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(totals.over90)}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(totals.outstanding)}</strong></td>
                    <td className="ims-td" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AccountsPayablePage;
